import { toast } from 'sonner';
import { GiftIcon } from 'lucide-react';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useSite } from '../../../contexts/SiteContext';
import { useCatalog } from '../../../contexts/CatalogContext';
import { dayShort } from '../../../utils/account';
import { subscriptionCadences, subscriptionRefill } from '../../../utils/refill';
import { euro } from '../../../core/format/currency';
import { IntervalChoice } from '../../shared/IntervalChoice';
import type { Subscription } from '../../../core/types/account';
type Props = {
  sub: Subscription;
  untilGift: number;
};

/**
 * Het pakketritme, met de komende dozen en wat erin zit. Alleen ritmes waarbij elke doos het
 * minimum haalt staan erbij (slim bijvullen, core/rules/refillPlan.ts); het huidige blijft altijd zichtbaar.
 */
export function SubscriptionRhythm({
  sub,
  untilGift
}: Props) {
  const {
    policy
  } = useSite();
  const {
    subscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    boxes
  } = subscriptionRefill(sub, productById, policy.subscription);
  const choices = subscriptionCadences(sub, productById, policy.subscription);
  return <section aria-labelledby="rhythm-title" className="bg-shell p-5 ring-1 ring-line sm:p-6" data-id="element-6187" data-mp="2bgcxzfmatkqf">
      <h2 id="rhythm-title" className="text-[15px] text-ink" data-id="element-6188" data-mp="26h0265dg93bp">
        Je ritme
      </h2>
      <p id="rhythm-hint" className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-6189" data-mp="21hnczdz3vk5y">
        Elke {sub.intervalWeeks} weken een doos, met alleen wat bijna op is. Zo staat er nooit een tweede crème op de plank.
      </p>
      {choices.length > 1 && <div className="mt-4" data-id="element-6190" data-mp="2pd9p7i6bx47u">
          <IntervalChoice intervals={choices} value={sub.intervalWeeks} labelledBy="rhythm-title" describedBy="rhythm-hint" onChange={async w => {
        if (await subscription({
          type: 'setInterval',
          weeks: w
        })) toast.success(`Ritme aangepast: elke ${w} weken een doos`);
      }} data-id="element-6191" data-mp="2pc6vz3ljapor" />
          <p className="mt-2 text-[12px] text-muted" data-id="element-6192" data-mp="1b5jyrjmox9qe">Alleen ritmes waarbij elke doos minstens {euro(policy.subscription.minBoxValue)} is.</p>
        </div>}
      <h3 className="sr-only" data-id="element-6193" data-mp="1g6pulqbndd45">Komende dozen</h3>
      <ol className="mt-5 divide-y divide-line border-t border-line" data-id="element-6194" data-mp="11ez4g8e1urda">
        {boxes.slice(0, 3).map((b, i) => <li key={b.date} className="py-3" data-id="element-6195" data-mp="16dsfzv9s08at" data-mpi={i}>
            <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-6196" data-mp="sfc1hi5o9di4">
              <span className="text-ink" data-id="element-6197" data-mp="shosekteqy7b">
                {dayShort(b.date)}
                {i + 1 === untilGift && <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-clay" data-id="element-6198" data-mp="ihecgsfzncog">
                    <GiftIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-6199" data-mp="nfzf0yipq8g2" /> met cadeau
                  </span>}
              </span>
              <span className="tabular-nums text-muted" data-id="element-6200" data-mp="2cvyt8u16uo2k">{b.lines.length ? euro(b.total) : 'geen doos'}</span>
            </p>
            {b.lines.length > 0 && <p className="mt-0.5 text-[13px] text-muted" data-id="element-6201" data-mp="663ylzgk2rjx">
                {b.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${productById(l.productId)?.name ?? l.productId}`).join(', ')}
              </p>}
          </li>)}
      </ol>
    </section>;
}